import { ref, onMounted } from "vue";
import { toast } from "vue3-toastify";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { initializeApp, getApps } from "firebase/app";
import api from "@/helper/api";
import http from "@/helper/http";

// Firebase config (should match main.js)
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
};

// Initialize Firebase app if not already initialized
let firebaseApp;
if (getApps().length === 0) {
  firebaseApp = initializeApp(firebaseConfig);
} else {
  firebaseApp = getApps()[0];
}

export function useWebNotifications() {
  const permissionGranted = ref(false);
  const permissionDenied = ref(false);
  const isSupported = ref(false);
  const fcmToken = ref(null);

  // Check if browser supports notifications
  function checkSupport() {
    isSupported.value = "Notification" in window && "serviceWorker" in navigator;
    if (isSupported.value) {
      permissionGranted.value = Notification.permission === "granted";
      permissionDenied.value = Notification.permission === "denied";
    }
    return isSupported.value;
  }

  // Request notification permission
  async function requestPermission() {
    if (!checkSupport()) {
      console.warn("This browser does not support notifications");
      toast.warning("Your browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      permissionGranted.value = true;
      await registerToken();
      return true;
    }

    if (Notification.permission === "denied") {
      permissionDenied.value = true;
      toast.info(
        "Notification permission was denied. Please enable it in your browser settings."
      );
      return false;
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        permissionGranted.value = true;
        permissionDenied.value = false;
        await registerToken();
        toast.success("Notification permissions enabled");
        return true;
      } else {
        permissionDenied.value = true;
        toast.info("Notification permission was denied");
        return false;
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      toast.error("Failed to request notification permission");
      return false;
    }
  }

  // Register FCM token with backend
  async function registerToken() {
    try {
      const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.warn("VAPID key not configured");
        return null;
      }

      // Check if Firebase config is available
      const areEnvVarsDefined = Object.values(firebaseConfig).every(
        (value) => value !== undefined
      );
      if (!areEnvVarsDefined) {
        console.warn("Firebase environment variables not fully defined");
        return null;
      }

      // Get messaging instance
      const messaging = getMessaging(firebaseApp);
      
      // Request permission and get token
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
      });

      if (token) {
        fcmToken.value = token;
        console.log("FCM Token:", token);
        
        // Register token with backend
        try {
          await http.post(api.register_push_notification_device_token, {
            device_fcm_token: token,
            device_platform: "WEB"
          });
          console.log("FCM token registered successfully");
        } catch (error) {
          console.error("Error registering FCM token with backend:", error);
          // Don't show toast error as it's not critical for user experience
        }
        
        // Set up foreground message handler
        onMessage(messaging, (payload) => {
          console.log("Message received in foreground:", payload);
          toast.info(payload.notification?.title || payload.data?.title || "New notification", {
            body: payload.notification?.body || payload.data?.body || "",
          });
        });
        
        return token;
      } else {
        console.warn("No FCM token available");
        return null;
      }
    } catch (error) {
      console.error("Error registering notification token:", error);
      // Don't show toast error for token registration failures
      return null;
    }
  }

  // Show a test notification (if permission granted)
  function showTestNotification(title = "Test", body = "This is a test notification") {
    if (!permissionGranted.value) {
      return;
    }

    try {
      new Notification(title, {
        body: body,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "test-notification",
      });
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  }

  // Initialize on mount
  onMounted(() => {
    checkSupport();
    if (Notification.permission === "granted") {
      registerToken();
    }
  });

  return {
    permissionGranted,
    permissionDenied,
    isSupported,
    fcmToken,
    requestPermission,
    registerToken,
    showTestNotification,
    checkSupport,
  };
}
